import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getTheme, DirectionContext } from './theme';
import { useTranslation } from './utils/i18n-utils';

// Layout
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Sales from './pages/Sales';
import Reports from './pages/Reports';
import CarManagement from './pages/CarManagement';
import TestAuth from './pages/TestAuth';
import UserManagement from './pages/UserManagement';

// Accounting Pages
import Employees from './pages/accounting/Employees';
import Expenses from './pages/accounting/Expenses';
import ExpenseCategories from './pages/accounting/ExpenseCategories';
import Invoices from './pages/accounting/Invoices';
import CashCount from './pages/accounting/CashCount';
import ProfitReport from './pages/accounting/ProfitReport';
import CashManagement from './pages/accounting/CashManagement';
import EmployeeShifts from './pages/accounting/EmployeeShifts';
import AccountingDashboard from './pages/accounting/AccountingDashboard';

function App() {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(
    i18n.language === 'ar' ? 'rtl' : 'ltr'
  );

  // Update direction when language changes
  useEffect(() => {
    setDirection(i18n.language === 'ar' ? 'rtl' : 'ltr');
  }, [i18n.language]);

  const toggleDirection = () => {
    setDirection((prev) => (prev === 'ltr' ? 'rtl' : 'ltr'));
  };

  // Create theme based on current direction
  const theme = getTheme(direction);

  return (
    <DirectionContext.Provider value={{ direction, toggleDirection }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/test-auth" element={<TestAuth />} />
                
                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/dashboard" element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="sales" element={<Sales />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="car-management" element={<CarManagement />} />
                    
                    {/* Admin routes */}
                    <Route path="users" element={<UserManagement />} />
                    
                    {/* Accounting routes */}
                    <Route path="accounting" element={<AccountingDashboard />} />
                    <Route path="accounting/employees" element={<Employees />} />
                    <Route path="accounting/expenses" element={<Expenses />} />
                    <Route path="accounting/expense-categories" element={<ExpenseCategories />} />
                    <Route path="accounting/invoices" element={<Invoices />} />
                    <Route path="accounting/cash-count" element={<CashCount />} />
                    <Route path="accounting/profit-report" element={<ProfitReport />} />
                    <Route path="accounting/cash-management" element={<CashManagement />} />
                    <Route path="accounting/employees/:employeeId/shifts" element={<EmployeeShifts />} />
                  </Route>
                </Route>
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </DirectionContext.Provider>
  );
}

export default App;
